import React, {useEffect, useState} from 'react';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import CellLoader from '@/components/loading/CellLoader';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import CompactList from '@/components/ui/CompactList';
import {BaseModal} from '@/components/modals';
import {Button} from '@/components/ui';
import i18n from 'i18n';
import Icon from '@/components/icons/Icon';
import {buildURL} from '@/utils/urlBuilder';
import {UserCanAccess} from 'entities/user';
import {getRestrictedFieldsDetailed, FieldRegistryEntry} from '@/utils/fieldRegistry';
import {isFieldRestricted} from '@/utils/fieldRestrictions';
import {RestrictedFieldIndicator} from '@/components/utils/DataNotAvailable';

const FieldRestrictionsList: React.FC<{items: FieldRegistryEntry[]}> = ({items}) => {
    const [showModal, setShowModal] = useState(false);
    const maxItems = 3;

    const displayItems = items.slice(0, maxItems);

    return (
        <>
            <p className="text-muted">
                {displayItems.map(item => item.label).join(', ')}
                {items.length > maxItems && (
                    <span
                        className="text-primary ml-2"
                        style={{cursor: 'pointer', textDecoration: 'underline'}}
                        onClick={() => setShowModal(true)}
                    >
                        ...total {items.length} items
                    </span>
                )}
            </p>

            <BaseModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={i18n.t('jt_field_restrictions.text', {defaultValue: 'Field Restrictions'})}
                size="lg"
                footer={
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        {i18n.t('jt_close.text')}
                    </Button>
                }
            >
                <div className="row">
                    {items.map((item, index) => (
                        <div key={index} className="col-md-6 mb-3">
                            <div className="fw-semibold">{item.label}</div>
                            <small className="text-muted">{item.fields.join(', ')}</small>
                        </div>
                    ))}
                </div>
            </BaseModal>
        </>
    );
};

const UserPermissionsView = React.memo(() => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const [access, setAccess] = useState<UserCanAccess | null>(null);
    const [loadingAccess, setLoadingAccess] = useState<boolean>(true);

    useEffect(() => {
        fetch(buildURL('currentUserAccessResource'), {
            credentials: 'same-origin',
            headers: {'Accept': 'application/json'},
        })
            .then(response => {
                if (!response.ok) {throw new Error('User access fetch failed: ' + response.status);}
                return response.json();
            })
            .then((data: UserCanAccess) => setAccess(data))
            .catch(err => {
                console.error('Failed to load user access lists:', err);
                setAccess({channels: [], agents: [], teams: []});
            })
            .finally(() => setLoadingAccess(false));
    }, []);

    const restrictedFieldsDetailed = getRestrictedFieldsDetailed(userData?.restrictedFieldKeys);
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const teamRestricted = isFieldRestricted('team_pii', restrictedFieldKeys);
    const agentRestricted = isFieldRestricted('agent_pii', restrictedFieldKeys);

    if (loadingUserData || loadingAccess) {
        return (
            <CellLoader />
        );
    }

    return (
        <div className="row">
            <div className="col-lg-12">
                <HelpTextBlock helpText={i18n.t('ht_permissions_are.text')} />

                <div className="card">
                    <div className="card-body pb-0">
                        <div className="d-flex no-block">
                            <h4 className="card-title">{i18n.t('jt_permissions.text')}</h4>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="mb-3">
                            <h5>{i18n.t('jt_channel_access.text')}</h5>
                            {access?.channels && access.channels.length > 0 ? (
                                <CompactList title={i18n.t('jt_channel_access_title.text')} items={access.channels} />
                            ) : (
                                <p className="text-muted fst-italic">
                                    <Icon name="mdi-information" className="mr-1 text-info" />
                                    {i18n.t('ttl_no_channel_access_granted.text')}
                                </p>
                            )}
                        </div>

                        <div className="mb-3">
                            <h5>{i18n.t('jt_team_access.text')}</h5>
                            {teamRestricted ? (
                                <RestrictedFieldIndicator />
                            ) : access?.teams && access.teams.length > 0 ? (
                                <CompactList title={i18n.t('jt_team_access_title.text')} items={access.teams} />
                            ) : (
                                <p className="text-muted fst-italic">
                                    <Icon name="mdi-information" className="mr-1 text-info" />
                                    {i18n.t('ttl_no_team_access_granted.text')}
                                </p>
                            )}
                        </div>

                        <div className="mb-3">
                            <h5>{i18n.t('jt_agent_access.text')}</h5>
                            {agentRestricted ? (
                                <RestrictedFieldIndicator />
                            ) : access?.agents && access.agents.length > 0 ? (
                                <CompactList title={i18n.t('jt_agent_access_title.text')} items={access.agents} />
                            ) : (
                                <p className="text-muted fst-italic">
                                    <Icon name="mdi-information" className="mr-1 text-info" />
                                    {i18n.t('ttl_no_agent_access_granted.text')}
                                </p>
                            )}
                        </div>

                        <div className="mb-3">
                            <h5>{i18n.t('th_groups.text')}</h5>
                            {userData?.groups && userData.groups.length > 0 ? (
                                <CompactList title={i18n.t('jt_groups_title.text')} items={userData?.groups || []} />
                            ) : (
                                <p className="text-muted fst-italic">
                                    <Icon name="mdi-information" className="mr-1 text-info" />
                                    {i18n.t('ttl_no_groups_assigned.text')}
                                </p>
                            )}
                        </div>

                        <div>
                            <h5>{i18n.t('jt_your_permissions.text')}</h5>
                            {userData?.permissions && userData.permissions.length > 0 ? (
                                <CompactList title={i18n.t('jt_your_permissions.text')} items={userData?.permissions || []} />
                            ) : (
                                <p className="text-muted fst-italic">
                                    <Icon name="mdi-information" className="mr-1 text-info" />
                                    {i18n.t('jt_no_permissions_assigned.text')}
                                </p>
                            )}
                        </div>
                        <div className="mb-3">
                            <h5>{i18n.t('jt_field_restrictions.text', {defaultValue: 'Field Restrictions'})}</h5>
                            {restrictedFieldsDetailed.length > 0 ? (
                                <FieldRestrictionsList items={restrictedFieldsDetailed} />
                            ) : (
                                <p className="text-muted fst-italic">
                                    <Icon name="mdi-information" className="mr-1 text-info" />
                                    {i18n.t('ttl_no_field_restrictions.text', {defaultValue: 'No field restrictions applied to your role'})}
                                </p>
                            )}
                        </div>

                        <div className="mb-3">
                            <h5>{i18n.t('jt_superuser.text')}</h5>
                            <p>
                                {userData?.isSuperuser ? (
                                    <span className="text-success">
                                        <Icon name="mdi-check" /> {i18n.t('jt_yes.text')}
                                    </span>
                                ) : (
                                    <span className="text-danger">
                                        <Icon name="mdi-close" /> {i18n.t('jt_no.text')}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default UserPermissionsView;
